import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Home, Truck, ChevronRight, Loader2, Save, X } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import VehicleRecordsTab from './VehicleRecordsTab';

const TABS = ['Basic Details', 'Documents', 'Maintenance'];

const empty = () => ({
  vendor_id: '', vehicle_name: '', vehicle_company: '', registration_number: '',
  vehicle_model: '', minus_temp_rate: '', plus_temp_rate: '', general_rate: '',
  cross_border_rate: '', local_temp: '', local_gen: '',
  vehicle_type_id: '', vehicle_size: '',
  chasis_no: '', engine_no: '', purchase_date: '', dimensions_ft: '',
  gross_weight: '', total_weight_acceptance: '',
  air_conditioned: false, national_permit: false,
  permit_issued_date: '', permit_expiry_date: '',
  owner_name: '', owner_pan: '', owner_aadhar: '',
  mobile_1: '', mobile_2: '', address: '', is_active: true
});

const VendorVehicleForm = () => {
  const navigate = useNavigate();
  const { id: vehicleId } = useParams();
  const { toast } = useToast();
  const isEdit = !!vehicleId;

  const [tab, setTab] = useState('Basic Details');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(empty());
  const [vendors, setVendors] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [vehicleSizes, setVehicleSizes] = useState([]);
  const [driveConnected, setDriveConnected] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json'
  }), []);

  const fetchMasters = useCallback(async () => {
    const [vRes, vtRes, drRes] = await Promise.all([
      fetch(`${backendUrl}/api/vendors`, { headers: getAuthHeaders() }),
      fetch(`${backendUrl}/api/vehicle-types`, { headers: getAuthHeaders() }),
      fetch(`${backendUrl}/api/drive/status`, { headers: getAuthHeaders() }).catch(() => null),
    ]);
    if (vRes.ok) { const v = await vRes.json(); setVendors(v.items || []); }
    if (vtRes.ok) setVehicleTypes(await vtRes.json());
    if (drRes?.ok) { const d = await drRes.json(); setDriveConnected(d.connected); }
  }, [backendUrl, getAuthHeaders]);

  const fetchVehicle = useCallback(async () => {
    if (!vehicleId) return;
    setLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/vendor-vehicles/${vehicleId}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Not found');
      const d = await res.json();
      setFormData({
        vendor_id: d.vendor_id || '', vehicle_name: d.vehicle_name || '',
        vehicle_company: d.vehicle_company || '', registration_number: d.registration_number || '',
        vehicle_model: d.vehicle_model || '', minus_temp_rate: d.minus_temp_rate ?? '',
        plus_temp_rate: d.plus_temp_rate ?? '', general_rate: d.general_rate ?? '',
        cross_border_rate: d.cross_border_rate ?? '', local_temp: d.local_temp ?? '',
        local_gen: d.local_gen ?? '', vehicle_type_id: d.vehicle_type_id || '',
        vehicle_size: d.vehicle_size || '', chasis_no: d.chasis_no || '',
        engine_no: d.engine_no || '', purchase_date: d.purchase_date || '',
        dimensions_ft: d.dimensions_ft || '', gross_weight: d.gross_weight || '',
        total_weight_acceptance: d.total_weight_acceptance || '',
        air_conditioned: d.air_conditioned || false, national_permit: d.national_permit || false,
        permit_issued_date: d.permit_issued_date || '', permit_expiry_date: d.permit_expiry_date || '',
        owner_name: d.owner_name || '', owner_pan: d.owner_pan || '',
        owner_aadhar: d.owner_aadhar || '', mobile_1: d.mobile_1 || '',
        mobile_2: d.mobile_2 || '', address: d.address || '', is_active: d.is_active
      });
      // Load sizes for selected type
      if (d.vehicle_type_id) {
        const vtRes = await fetch(`${backendUrl}/api/vehicle-types/${d.vehicle_type_id}`, { headers: getAuthHeaders() });
        if (vtRes.ok) { const vt = await vtRes.json(); setVehicleSizes(vt.sizes || []); }
      }
    } catch { toast({ title: "Error", description: "Failed to load vehicle", variant: "destructive" }); navigate('/fleet/vehicles'); }
    finally { setLoading(false); }
  }, [vehicleId, backendUrl, getAuthHeaders, toast, navigate]);

  useEffect(() => { fetchMasters(); }, [fetchMasters]);
  useEffect(() => { if (isEdit) fetchVehicle(); }, [isEdit, fetchVehicle]);

  const set = (field, value) => setFormData(p => ({ ...p, [field]: value }));

  // Called by VehicleRecordsTab when Attestr RC data is applied
  const handleRcDataFetched = useCallback(({ engine_no, chasis_no }) => {
    let updated = false;
    setFormData(prev => {
      const next = { ...prev };
      if (engine_no && !prev.engine_no) { next.engine_no = engine_no; updated = true; }
      if (chasis_no && !prev.chasis_no) { next.chasis_no = chasis_no; updated = true; }
      return next;
    });
    if (engine_no || chasis_no) {
      toast({
        title: 'Vehicle Details Updated',
        description: `${[engine_no && 'Engine No.', chasis_no && 'Chassis No.'].filter(Boolean).join(' & ')} updated from RC data. Save the vehicle to persist.`,
      });
    }
  }, [toast]);

  const handleTypeChange = async (typeId) => {
    set('vehicle_type_id', typeId);
    set('vehicle_size', '');
    if (!typeId) { setVehicleSizes([]); return; }
    try {
      const res = await fetch(`${backendUrl}/api/vehicle-types/${typeId}`, { headers: getAuthHeaders() });
      if (res.ok) { const vt = await res.json(); setVehicleSizes(vt.sizes || []); }
    } catch { setVehicleSizes([]); }
  };

  const handleSave = async () => {
    if (!formData.vendor_id) { toast({ title: "Error", description: "Vendor is required", variant: "destructive" }); return; }
    if (!formData.registration_number.trim()) { toast({ title: "Error", description: "Registration number is required", variant: "destructive" }); return; }
    if (!formData.vehicle_type_id) { toast({ title: "Error", description: "Vehicle Type is required", variant: "destructive" }); return; }
    if (!formData.vehicle_size) { toast({ title: "Error", description: "Vehicle Size is required", variant: "destructive" }); return; }
    try {
      setSaving(true);
      const payload = {
        ...formData,
        minus_temp_rate: formData.minus_temp_rate !== '' ? parseFloat(formData.minus_temp_rate) : null,
        plus_temp_rate: formData.plus_temp_rate !== '' ? parseFloat(formData.plus_temp_rate) : null,
        general_rate: formData.general_rate !== '' ? parseFloat(formData.general_rate) : null,
        cross_border_rate: formData.cross_border_rate !== '' ? parseFloat(formData.cross_border_rate) : null,
        local_temp: formData.local_temp !== '' ? parseFloat(formData.local_temp) : null,
        local_gen: formData.local_gen !== '' ? parseFloat(formData.local_gen) : null,
        purchase_date: formData.purchase_date || null,
        permit_issued_date: formData.permit_issued_date || null,
        permit_expiry_date: formData.permit_expiry_date || null,
        vehicle_type_name: vehicleTypes.find(t => t.id === formData.vehicle_type_id)?.type_name || null,
      };
      const res = await fetch(
        isEdit ? `${backendUrl}/api/vendor-vehicles/${vehicleId}` : `${backendUrl}/api/vendor-vehicles`,
        { method: isEdit ? 'PUT' : 'POST', headers: getAuthHeaders(), body: JSON.stringify(payload) }
      );
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Failed to save'); }
      const saved = await res.json();
      toast({ title: "Success", description: isEdit ? "Vehicle updated" : "Vehicle created" });
      if (!isEdit) navigate(`/fleet/vehicles/edit/${saved.id}`);
    } catch (e) { toast({ title: "Error", description: e.message, variant: "destructive" }); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm font-ibm">
        <Home className="w-4 h-4 text-text-secondary" />
        <ChevronRight className="w-4 h-4 text-text-secondary" />
        <span className="text-text-secondary cursor-pointer hover:text-accent-blue" onClick={() => navigate('/fleet/vehicles')}>Vehicles</span>
        <ChevronRight className="w-4 h-4 text-text-secondary" />
        <span className="text-accent-blue font-medium">{isEdit ? 'Edit Vehicle' : 'Add Vehicle'}</span>
      </div>

      <h1 className="text-3xl font-bold text-text-dark font-manrope">{isEdit ? 'Edit Vehicle' : 'Add Vehicle'}</h1>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        {TABS.map(t => (
          <button key={t} onClick={() => (!isEdit && t !== 'Basic Details') ? null : setTab(t)}
            data-testid={`tab-${t.toLowerCase().replace(/\s+/g,'-')}`}
            className={`px-6 py-3 text-sm font-medium font-ibm border-b-2 transition-colors ${
              tab === t ? 'border-accent-blue text-accent-blue' : 'border-transparent text-text-secondary'
            } ${!isEdit && t !== 'Basic Details' ? 'opacity-40 cursor-not-allowed' : 'hover:text-accent-blue'}`}
          >{t} {!isEdit && t !== 'Basic Details' && <span className="text-xs">(save first)</span>}</button>
        ))}
      </div>

      {/* ===== BASIC DETAILS TAB ===== */}
      {tab === 'Basic Details' && (
        <div className="space-y-5">
          {/* Vendor + Identification */}
          <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
            <div className="border-b-4 border-accent-blue px-6 py-4">
              <h3 className="text-sm font-semibold text-text-dark font-manrope uppercase tracking-wide">Vehicle Identification</h3>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium font-ibm text-text-dark">Vendor <span className="text-accent-red">*</span></Label>
                <Select value={formData.vendor_id} onValueChange={v => set('vendor_id', v)}>
                  <SelectTrigger data-testid="vehicle-vendor-select" className="font-ibm border-gray-200"><SelectValue placeholder="Select Vendor" /></SelectTrigger>
                  <SelectContent>{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.business_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium font-ibm text-text-dark">Vehicle Name</Label>
                <Input data-testid="vehicle-name-input" value={formData.vehicle_name} onChange={e => set('vehicle_name', e.target.value)} placeholder="Vehicle Name" className="border-gray-200 font-ibm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium font-ibm text-text-dark">Reg. Number <span className="text-accent-red">*</span></Label>
                <Input data-testid="vehicle-reg-input" value={formData.registration_number} onChange={e => set('registration_number', e.target.value.toUpperCase())} placeholder="e.g. GJ01AB1234" className="border-gray-200 font-ibm uppercase" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium font-ibm text-text-dark">Vehicle Company</Label>
                <Input data-testid="vehicle-company-input" value={formData.vehicle_company} onChange={e => set('vehicle_company', e.target.value)} placeholder="e.g. TATA, Ashok Leyland" className="border-gray-200 font-ibm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium font-ibm text-text-dark">Vehicle Model</Label>
                <Input value={formData.vehicle_model} onChange={e => set('vehicle_model', e.target.value)} placeholder="Model" className="border-gray-200 font-ibm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium font-ibm text-text-dark">Vehicle Type <span className="text-accent-red">*</span></Label>
                <Select value={formData.vehicle_type_id} onValueChange={handleTypeChange}>
                  <SelectTrigger data-testid="vehicle-type-select" className="font-ibm border-gray-200"><SelectValue placeholder="Select Type" /></SelectTrigger>
                  <SelectContent>{vehicleTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.type_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium font-ibm text-text-dark">Vehicle Size <span className="text-accent-red">*</span></Label>
                <Select value={formData.vehicle_size} onValueChange={v => set('vehicle_size', v)} disabled={!formData.vehicle_type_id}>
                  <SelectTrigger data-testid="vehicle-size-select" className="font-ibm border-gray-200"><SelectValue placeholder="Select Size" /></SelectTrigger>
                  <SelectContent>{vehicleSizes.map(s => <SelectItem key={s.id} value={s.size_name}>{s.size_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium font-ibm text-text-dark">Purchase Date</Label>
                <Input type="date" value={formData.purchase_date} onChange={e => set('purchase_date', e.target.value)} className="border-gray-200 font-ibm" />
              </div>
            </div>
          </Card>

          {/* Rates */}
          <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
            <div className="border-b-4 border-accent-blue px-6 py-4">
              <h3 className="text-sm font-semibold text-text-dark font-manrope uppercase tracking-wide">Rate Configuration</h3>
            </div>
            <div className="p-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
              {[
                ['minus_temp_rate','Minus Temp Rate'],['plus_temp_rate','Plus Temp Rate'],
                ['general_rate','General Rate'],['cross_border_rate','Cross Border Rate'],
                ['local_temp','Local Temp'],['local_gen','Local Gen']
              ].map(([field, label]) => (
                <div key={field} className="space-y-1.5">
                  <Label className="text-xs font-medium font-ibm text-text-dark">{label}</Label>
                  <Input type="number" value={formData[field]} onChange={e => set(field, e.target.value)} placeholder="0.00" className="border-gray-200 font-ibm" />
                </div>
              ))}
            </div>
          </Card>

          {/* Technical */}
          <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
            <div className="border-b-4 border-accent-blue px-6 py-4">
              <h3 className="text-sm font-semibold text-text-dark font-manrope uppercase tracking-wide">Technical Details</h3>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              <div className="space-y-1.5"><Label className="text-sm font-medium font-ibm text-text-dark">Chasis No.</Label>
                <Input value={formData.chasis_no} onChange={e => set('chasis_no', e.target.value)} className="border-gray-200 font-ibm" /></div>
              <div className="space-y-1.5"><Label className="text-sm font-medium font-ibm text-text-dark">Engine No.</Label>
                <Input value={formData.engine_no} onChange={e => set('engine_no', e.target.value)} className="border-gray-200 font-ibm" /></div>
              <div className="space-y-1.5"><Label className="text-sm font-medium font-ibm text-text-dark">Dimensions (ft)</Label>
                <Input value={formData.dimensions_ft} onChange={e => set('dimensions_ft', e.target.value)} placeholder="e.g. 14×7×7" className="border-gray-200 font-ibm" /></div>
              <div className="space-y-1.5"><Label className="text-sm font-medium font-ibm text-text-dark">Gross Weight</Label>
                <Input value={formData.gross_weight} onChange={e => set('gross_weight', e.target.value)} className="border-gray-200 font-ibm" /></div>
              <div className="space-y-1.5"><Label className="text-sm font-medium font-ibm text-text-dark">Total Weight Acceptance</Label>
                <Input value={formData.total_weight_acceptance} onChange={e => set('total_weight_acceptance', e.target.value)} className="border-gray-200 font-ibm" /></div>
              <div className="space-y-1.5"><Label className="text-sm font-medium font-ibm text-text-dark">Air Conditioned</Label>
                <Select value={formData.air_conditioned ? 'yes' : 'no'} onValueChange={v => set('air_conditioned', v === 'yes')}>
                  <SelectTrigger className="border-gray-200 font-ibm"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label className="text-sm font-medium font-ibm text-text-dark">National Permit</Label>
                <Select value={formData.national_permit ? 'yes' : 'no'} onValueChange={v => set('national_permit', v === 'yes')}>
                  <SelectTrigger className="border-gray-200 font-ibm"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
                </Select></div>
              {formData.national_permit && <>
                <div className="space-y-1.5"><Label className="text-sm font-medium font-ibm text-text-dark">Permit Issued Date</Label>
                  <Input type="date" value={formData.permit_issued_date} onChange={e => set('permit_issued_date', e.target.value)} className="border-gray-200 font-ibm" /></div>
                <div className="space-y-1.5"><Label className="text-sm font-medium font-ibm text-text-dark">Permit Expiry Date</Label>
                  <Input type="date" value={formData.permit_expiry_date} onChange={e => set('permit_expiry_date', e.target.value)} className="border-gray-200 font-ibm" /></div>
              </>}
            </div>
          </Card>

          {/* Ownership */}
          <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
            <div className="border-b-4 border-accent-blue px-6 py-4">
              <h3 className="text-sm font-semibold text-text-dark font-manrope uppercase tracking-wide">Owner Details</h3>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              <div className="space-y-1.5"><Label className="text-sm font-medium font-ibm text-text-dark">Owner Name</Label>
                <Input data-testid="vehicle-owner-name" value={formData.owner_name} onChange={e => set('owner_name', e.target.value)} className="border-gray-200 font-ibm" /></div>
              <div className="space-y-1.5"><Label className="text-sm font-medium font-ibm text-text-dark">Owner PAN No.</Label>
                <Input value={formData.owner_pan} onChange={e => set('owner_pan', e.target.value.toUpperCase())} maxLength={10} className="border-gray-200 font-ibm uppercase" /></div>
              <div className="space-y-1.5"><Label className="text-sm font-medium font-ibm text-text-dark">Owner Aadhar No.</Label>
                <Input value={formData.owner_aadhar} onChange={e => set('owner_aadhar', e.target.value.replace(/\D/g,''))} maxLength={12} className="border-gray-200 font-ibm" /></div>
              <div className="space-y-1.5"><Label className="text-sm font-medium font-ibm text-text-dark">Mobile 1</Label>
                <Input data-testid="vehicle-mobile1" type="tel" value={formData.mobile_1} onChange={e => set('mobile_1', e.target.value.replace(/\D/g,''))} maxLength={10} className="border-gray-200 font-ibm" /></div>
              <div className="space-y-1.5"><Label className="text-sm font-medium font-ibm text-text-dark">Mobile 2</Label>
                <Input type="tel" value={formData.mobile_2} onChange={e => set('mobile_2', e.target.value.replace(/\D/g,''))} maxLength={10} className="border-gray-200 font-ibm" /></div>
              <div className="space-y-1.5 col-span-2"><Label className="text-sm font-medium font-ibm text-text-dark">Address</Label>
                <Textarea value={formData.address} onChange={e => set('address', e.target.value)} className="border-gray-200 font-ibm min-h-[70px]" /></div>
            </div>
          </Card>

          {/* Actions */}
          <div className="flex gap-4 justify-end">
            <Button variant="outline" onClick={() => navigate('/fleet/vehicles')} className="font-ibm font-semibold h-11 px-8"><X className="w-4 h-4 mr-2" />Cancel</Button>
            <Button data-testid="save-vehicle-btn" onClick={handleSave} disabled={saving} className="bg-accent-blue hover:bg-accent-blue/90 text-white font-ibm font-semibold h-11 px-8">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />{isEdit ? 'Update' : 'Save Vehicle'}</>}
            </Button>
          </div>
        </div>
      )}

      {/* ===== DOCUMENTS TAB ===== */}
      {tab === 'Documents' && isEdit && (
        <Card className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <p className="text-sm text-text-secondary font-ibm mb-4">Manage compliance documents for this vehicle. Expired entries are highlighted in red.</p>
          <VehicleRecordsTab vehicleId={vehicleId} category="documents" backendUrl={backendUrl} getAuthHeaders={getAuthHeaders} driveConnected={driveConnected} registrationNumber={formData.registration_number} onRcDataFetched={handleRcDataFetched} />
        </Card>
      )}

      {/* ===== MAINTENANCE TAB ===== */}
      {tab === 'Maintenance' && isEdit && (
        <Card className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <p className="text-sm text-text-secondary font-ibm mb-4">Track service, tyre changes, battery changes, urea, and general maintenance records.</p>
          <VehicleRecordsTab vehicleId={vehicleId} category="maintenance" backendUrl={backendUrl} getAuthHeaders={getAuthHeaders} driveConnected={driveConnected} />
        </Card>
      )}
    </div>
  );
};

export default VendorVehicleForm;
